import { useEffect, useMemo, useState } from 'react'
import { useAuthSession } from '../../lib/authSession'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import type { RtSegment } from '../RecordTable'
import ObjectDrawer, { type DrawerTarget } from '../ObjectDrawer'
import { manualMovementHref } from '../manualMovement'
import FundRecordTab from '../fund/FundRecordTab'
import PendingRecordEdits from '../recordEdits/PendingRecordEdits'
import AdditionalRecordObjects from './AdditionalRecordObjects'
import SourceObjectDossier from './SourceObjectDossier'
import ObjectContextNavigation from './ObjectContextNavigation'
import ObjectRecordEditor from './ObjectRecordEditor'
import { recordEvidenceFacts } from './recordEvidenceContext'
import { objectContextNavigation, objectWorkContext, type FundRelationships } from './objectContext'
import type { MatrixRow } from '../fund/fundArea'
import { ErrorState } from '../state'
import {
  additionalObjectAccess,
  cardForPosition,
  evidencedRecordContext,
  recordContents,
  recordCsv,
  recordHref,
  recordQuery,
  recordTarget,
  scopeFromParams,
  subchainScope,
  type RecordContext,
  type ViewScope,
} from './context'
import { useContextualNavigation, useRead } from './useRead'
import '../fund/fundtabs.css'
import './navigation.css'

function ScopeRecord({
  scope,
  asOf,
  evidenced,
}: {
  scope: ViewScope
  asOf: string | null
  evidenced: boolean
}) {
  const [retry, setRetry] = useState(0)
  const [params] = useSearchParams()
  const rawFocus = params.get('focus')
  const [drawerFund, setDrawerFund] = useState<string | null>(null)
  const navigate = useNavigate()
  // Read one complete confirmed snapshot; evidence changes display membership only.
  // The server has already applied the requested financial as-of cutoff.
  const url = recordQuery(scope, { asOf })
  const read = useRead<RecordContext>(url, retry)
  const session = useAuthSession()
  const viewKey = JSON.stringify([url, session])
  const [retained, setRetained] = useState<{ key: string; value: RecordContext } | null>(null)
  useEffect(() => {
    if (read.error) setRetained(null)
    else if (read.value) setRetained({ key: viewKey, value: read.value })
  }, [read.value, read.error, viewKey])
  // Refresh the Record without destroying the exact edit/result being reviewed.
  // A different login/scope and a failed read never inherit this prior view.
  const fullValue =
    read.value ?? (read.loading && retained?.key === viewKey ? retained.value : undefined)
  const fullTable = useMemo(() => (fullValue ? recordContents(fullValue) : null), [fullValue])
  const value = useMemo(
    () => (fullValue && evidenced ? evidencedRecordContext(fullValue) : fullValue),
    [fullValue, evidenced]
  )
  const table = useMemo(
    () => (value ? recordContents(value, fullTable?.counts) : null),
    [value, fullTable]
  )
  const target = table ? recordTarget(rawFocus, table) : null
  const additionalAccess = value && target ? additionalObjectAccess(value, target, asOf) : null
  const editLabel =
    target && table
      ? target.type === 'entity'
        ? table.entities.find((row) => row.id === target.id)?.name
        : target.type === 'instrument'
          ? table.instruments.find((row) => row.id === target.id)?.name
          : target.type === 'trade'
            ? table.trades.find((row) => row.id === target.id)?.transaction_type.replace(/_/g, ' ')
            : table.positions.find((row) => row.id === target.id)?.instrument_name
      : null
  const additionalCount = value?.additional_objects?.counts.recorded ?? 0
  const closeTarget = () => navigate(recordHref(scope, { asOf, evidenced }))
  const matrix = useMemo(() => {
    const rows = new Map<string, MatrixRow>()
    for (const record of value?.records ?? [])
      for (const row of record.matrix as unknown as MatrixRow[])
        rows.set(`${row.object_type}:${row.object_id}:${row.obligation_code}`, row)
    return [...rows.values()]
  }, [value])
  const tasks = (value?.work.tasks ?? []).map((task) => ({
    task_id: task.id,
    task_type: task.task_type,
    target_id: task.target_id,
  }))
  const label =
    value?.client?.display_name ??
    (scope.kind === 'all'
      ? 'All records'
      : scope.kind === 'subchain'
        ? value?.work.cards.find((card) => card.id === scope.subchain_id)?.label
        : value?.records[0]?.record.fund_name) ??
    'Record'
  const scopeLink = (next: ViewScope) => recordHref(next, { asOf, evidenced })
  const open = (selected: DrawerTarget) => {
    navigate(recordHref(scope, { asOf, evidenced, focus: `${selected.type}:${selected.id}` }))
    setDrawerFund(scope.fund_id ?? null)
  }
  const targetFunds =
    target && value
      ? value.records.filter((row) => {
          if (target.type === 'entity')
            return row.record.entities.some((item) => item.id === target.id)
          if (target.type === 'instrument')
            return row.record.instruments.some((item) => item.id === target.id)
          if (target.type === 'position')
            return row.record.positions.some((item) => item.id === target.id)
          return row.record.trades.some((item) => item.id === target.id)
        })
      : []
  const effectiveDrawerFund =
    drawerFund && targetFunds.some((row) => row.fund_id === drawerFund)
      ? drawerFund
      : targetFunds.length === 1
        ? targetFunds[0].fund_id
        : null
  const drawerScope: ViewScope | null = effectiveDrawerFund
    ? {
        kind: 'fund',
        fund_id: effectiveDrawerFund,
        client_id: scope.client_id,
      }
    : null
  const relationships = useRead<FundRelationships>(
    drawerScope && !asOf && target && ['entity', 'instrument'].includes(target.type)
      ? `/api/funds/${encodeURIComponent(drawerScope.fund_id!)}/masters?contract=relationships_v1`
      : null,
    retry
  )
  const objectNavigation =
    value && target && drawerScope && !asOf
      ? objectContextNavigation(value, target, drawerScope, relationships.value)
      : null
  const positionHref = (segment: RtSegment) => {
    const card = value && cardForPosition(value.work, segment.position_id)
    if (!card) throw new Error('The current subchain context is unavailable')
    return scopeLink(subchainScope(card, scope))
  }
  const exportRows = (segments: RtSegment[]) => {
    const blob = new Blob(['\ufeff', recordCsv(segments)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `record${asOf ? '-' + asOf : ''}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }
  return (
    <main
      className="detail fund-area nav-record fa"
      data-testid="context-record"
      data-revision={value?.revision}
    >
      <header className="fa-head">
        <nav className="nav-context-links" aria-label="Record scope">
          <Link to="/v2/funds">Funds</Link>
          <Link to={scopeLink({ kind: 'all' })}>All records</Link>
          {scope.client_id ? (
            <Link to={scopeLink({ kind: 'client', client_id: scope.client_id })}>
              Client records
            </Link>
          ) : null}
          {scope.fund_id ? (
            <Link
              to={scopeLink({ kind: 'fund', fund_id: scope.fund_id, client_id: scope.client_id })}
            >
              Fund record
            </Link>
          ) : null}
        </nav>
        <h1 className="fa-name">Record</h1>
        <p>{label}</p>
        <div className="nav-record-controls">
          <label>
            As of{' '}
            <input
              aria-label="Record as of"
              type="date"
              value={asOf ?? ''}
              onChange={(event) =>
                navigate(recordHref(scope, { asOf: event.target.value || null, evidenced }))
              }
            />
          </label>
          <Link to={recordHref(scope)}>Current recorded view</Link>
          <Link to={recordHref({ kind: 'all' })}>Reset scope and filters</Link>
        </div>
      </header>
      {read.loading ? <p role="status">Loading this record…</p> : null}
      {read.error ? (
        <ErrorState
          what="scoped record"
          error={read.error}
          onRetry={() => setRetry((n) => n + 1)}
        />
      ) : null}
      {value && table ? (
        <>
          {rawFocus && !target ? (
            <p className="nav-muted">The selected object is not part of this record view.</p>
          ) : null}
          {value.client_match_is_provisional ? (
            <p className="nav-muted">Client grouping uses provisional folder matches.</p>
          ) : null}
          {scope.kind === 'client' && value.fund_ids.length === 0 && additionalCount === 0 ? (
            <p>No uniquely matched registered fund records in this client context.</p>
          ) : null}
          <div className="nav-context-links" aria-label="Related fund records">
            {value.records.map((row) => (
              <Link
                key={row.fund_id}
                to={scopeLink({ kind: 'fund', fund_id: row.fund_id, client_id: scope.client_id })}
              >
                {row.record.fund_name} ›
              </Link>
            ))}
          </div>
          {table.entities
            .filter((entity) => entity.is_fund_root)
            .map((entity) => (
              <div className="nav-record-head" key={entity.id}>
                <button
                  className="nav-link"
                  onClick={() => open({ type: 'entity', id: entity.id })}
                >
                  {entity.name}
                </button>
              </div>
            ))}
          <PendingRecordEdits
            edits={value.pending_edits ?? []}
            table={table}
            matrix={matrix}
            current={!asOf && !evidenced}
            currentHref={recordHref(scope)}
            onRecordChanged={() => setRetry((n) => n + 1)}
          />
          <AdditionalRecordObjects
            value={value}
            table={table}
            asOf={asOf}
            evidenced={evidenced}
            onOpen={open}
          />
          {value.records.length > 0 || additionalCount === 0 ? (
            <FundRecordTab
              key={url}
              fundId={scope.fund_id ?? undefined}
              table={table}
              fullTable={fullTable ?? undefined}
              matrix={matrix}
              tasks={tasks}
              allowEdits={!asOf && !evidenced}
              onRecordChanged={() => setRetry((n) => n + 1)}
              editScope={(segment) => {
                const actualFunds = value.records
                  .filter((item) =>
                    item.record.positions.some((position) => position.id === segment.position_id)
                  )
                  .map((item) => item.fund_id)
                const fundId =
                  scope.fund_id && actualFunds.includes(scope.fund_id)
                    ? scope.fund_id
                    : actualFunds.length === 1
                      ? actualFunds[0]
                      : null
                const admitted = fundId
                  ? { fund_id: fundId }
                  : scope.kind === 'client' && scope.client_id
                    ? { client_id: scope.client_id }
                    : null
                return admitted && segment.instrument_id && segment.issuer_id
                  ? {
                      ...admitted,
                      holder_id: segment.holder_id,
                      instrument_id: segment.instrument_id,
                      issuer_id: segment.issuer_id,
                    }
                  : null
              }}
              lens={evidenced ? { mode: 'evidenced', label: 'Evidenced records' } : null}
              contextual={{
                onPosition: (segment) => navigate(positionHref(segment)),
                breakHref: positionHref,
                onExport: exportRows,
              }}
              onOpen={open}
              onEvidence={open}
            />
          ) : null}
          {value.broader_work ? (
            <p>
              <Link
                to={scopeLink({
                  kind: 'fund',
                  fund_id: value.broader_work.fund_id,
                  client_id: scope.client_id,
                })}
              >
                Return to full fund record ›
              </Link>
            </p>
          ) : null}
          {target && additionalAccess ? (
            <SourceObjectDossier
              key={`${viewKey}:${target.type}:${target.id}:${additionalAccess.dossier_url}`}
              access={additionalAccess}
              target={target}
              scope={scope}
              returnTo={recordHref(scope, {
                asOf,
                evidenced,
                focus: `${target.type}:${target.id}`,
              })}
              allowEdits={!asOf && !evidenced}
              onRecordChanged={() => setRetry((n) => n + 1)}
              onClose={closeTarget}
            />
          ) : null}
          {target && !additionalAccess && !effectiveDrawerFund && targetFunds.length === 0 ? (
            <p className="nav-muted" role="status">
              This object's details are unavailable in this view.
            </p>
          ) : null}
          {target && !additionalAccess && !effectiveDrawerFund && targetFunds.length > 0 ? (
            <aside className="nav-context-links" aria-label="Choose fund context">
              <button className="nav-link" onClick={closeTarget}>
                Close ×
              </button>
              <span>Choose the fund context:</span>
              {targetFunds.map((row) => (
                <button
                  className="nav-link"
                  key={row.fund_id}
                  onClick={() => setDrawerFund(row.fund_id)}
                >
                  {row.record.fund_name} ›
                </button>
              ))}
            </aside>
          ) : null}
          {target && !additionalAccess && effectiveDrawerFund ? (
            <ObjectDrawer
              key={viewKey + ':' + effectiveDrawerFund + ':' + target.type + ':' + target.id}
              fundId={effectiveDrawerFund}
              target={target}
              addMovementHref={
                !asOf && !evidenced && (target.type === 'position' || target.type === 'instrument')
                  ? manualMovementHref(effectiveDrawerFund, { kind: target.type, id: target.id })
                  : undefined
              }
              workContext={
                objectNavigation && drawerScope
                  ? objectWorkContext(value, drawerScope, objectNavigation)
                  : undefined
              }
              contextNavigation={
                objectNavigation ? (
                  <ObjectContextNavigation
                    navigation={objectNavigation}
                    loading={relationships.loading}
                    unavailable={!!relationships.error}
                    partial={relationships.value?.scope_incomplete}
                    onRetry={() => setRetry((n) => n + 1)}
                  />
                ) : null
              }
              recordRevision={retry}
              evidenceContext={
                !asOf && !evidenced && objectNavigation && drawerScope
                  ? {
                      cards: recordEvidenceFacts(
                        value,
                        drawerScope,
                        target,
                        scope.kind === 'subchain' ? (scope.subchain_id ?? undefined) : undefined
                      ),
                      available: !read.loading && !read.error,
                      onChanged: () => setRetry((n) => n + 1),
                    }
                  : undefined
              }
              recordEditor={
                !asOf && !evidenced ? (
                  <ObjectRecordEditor
                    target={{ kind: target.type, id: target.id, label: editLabel ?? target.type }}
                    scope={{ fund_id: effectiveDrawerFund }}
                    documents={matrix
                      .filter(
                        (row) =>
                          row.object_type === target.type &&
                          row.object_id === target.id &&
                          row.document_id
                      )
                      .map((row) => ({
                        document_id: row.document_id!,
                        display_title: row.document_name,
                        page: row.page,
                      }))}
                    onRecordChanged={() => setRetry((n) => n + 1)}
                  />
                ) : null
              }
              allowRemoval={!asOf && !evidenced}
              removalRequestIds={(value.work.removal_requests ?? [])
                .filter(
                  (request) =>
                    request.target.kind === target.type && request.target.id === target.id
                )
                .map((request) => request.id)}
              onRemovalRequested={() => setRetry((n) => n + 1)}
              onRemoved={() => {
                closeTarget()
                setRetry((n) => n + 1)
              }}
              onClose={closeTarget}
              onShowOnDiagram={() =>
                navigate(
                  `/v2/funds/${effectiveDrawerFund}?focus=${encodeURIComponent(target.type + ':' + target.id)}`
                )
              }
              onShowInRecord={closeTarget}
            />
          ) : null}
        </>
      ) : null}
    </main>
  )
}
export default function ContextRecordPage() {
  const [params] = useSearchParams()
  const capability = useContextualNavigation()
  let scope: ViewScope
  try {
    scope = scopeFromParams(params)
    if (params.has('state') && !['recorded', 'evidenced'].includes(params.get('state')!))
      throw new Error('Unknown record state')
  } catch {
    return (
      <main className="detail">
        <h1>Record context unavailable</h1>
        <p>The link does not identify a valid viewing scope.</p>
        <Link to="/v2/funds">Return to Funds</Link>
      </main>
    )
  }
  if (capability.error)
    return (
      <main className="detail">
        <ErrorState what="record navigation" error={capability.error} />
      </main>
    )
  if (capability.loading)
    return (
      <main className="detail" role="status">
        Loading record navigation…
      </main>
    )
  if (!capability.value?.enabled)
    return (
      <main className="detail">
        <h1 className="fa-name">Record</h1>
        <Link to="/v2/funds">Open a fund record from Funds ›</Link>
      </main>
    )
  // Retired filter links retain their scope, date and selected object.
  if (params.get('state') === 'evidenced') {
    const current = new URLSearchParams(params)
    current.delete('state')
    return <Navigate to={{ search: current.toString() }} replace />
  }
  return (
    <ScopeRecord
      key={recordQuery(scope, {
        asOf: params.get('asof'),
        evidenced: false,
      })}
      scope={scope}
      asOf={params.get('asof')}
      evidenced={false}
    />
  )
}
